import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CreateCampaignModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    discountType: '',
    discountValue: '',
    startDate: '',
    endDate: '',
    targetSegment: '',
    budget: ''
  });

  const discountTypeOptions = [
    { value: 'percentage', label: 'Percentage Off' },
    { value: 'fixed', label: 'Fixed Amount Off' },
    { value: 'bogo', label: 'Buy One Get One' },
    { value: 'cashback', label: 'Cashback Offer' }
  ];

  const targetSegmentOptions = [
    { value: 'all', label: 'All Customers' },
    { value: 'high-spenders', label: 'High Spenders' },
    { value: 'budget-conscious', label: 'Budget Conscious' },
    { value: 'new-customers', label: 'New Customers' },
    { value: 'loyal-customers', label: 'Loyal Customers' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (formData?.name && formData?.discountType && formData?.startDate && formData?.endDate) {
      onSave(formData);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-start justify-center pt-[8vh] px-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-card rounded-2xl shadow-elevation-5 p-6 md:p-8 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">Create New Campaign</h2>
            <p className="text-sm text-muted-foreground mt-1">Set up discount configuration and targeting parameters</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="space-y-5">
          <Input
            label="Campaign Name"
            type="text"
            required
            placeholder="Enter campaign name"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
          />

          <Input
            label="Description"
            type="text"
            placeholder="Brief description of the campaign"
            value={formData?.description}
            onChange={(e) => handleChange('description', e?.target?.value)}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Discount Type"
              required
              options={discountTypeOptions}
              value={formData?.discountType}
              onChange={(value) => handleChange('discountType', value)}
              placeholder="Select discount type"
            />

            <Input
              label="Discount Value"
              type="text"
              required
              placeholder="e.g., 20% or $50"
              value={formData?.discountValue}
              onChange={(e) => handleChange('discountValue', e?.target?.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              required
              value={formData?.startDate}
              onChange={(e) => handleChange('startDate', e?.target?.value)}
            />

            <Input
              label="End Date"
              type="date"
              required
              value={formData?.endDate}
              onChange={(e) => handleChange('endDate', e?.target?.value)}
            />
          </div>

          <Select
            label="Target Customer Segment"
            description="Choose which customer group to target"
            required
            options={targetSegmentOptions}
            value={formData?.targetSegment}
            onChange={(value) => handleChange('targetSegment', value)}
            placeholder="Select target segment"
          />

          <Input
            label="Campaign Budget"
            type="text"
            placeholder="e.g., $5,000"
            value={formData?.budget}
            onChange={(e) => handleChange('budget', e?.target?.value)}
            description="Optional: Set a maximum budget for this campaign"
          />

          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Lightbulb" size={18} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
              <div className="text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">Campaign Tips:</p>
                <p>• Target specific segments for better conversion rates</p>
                <p>• Set realistic budgets based on historical performance</p>
                <p>• Monitor campaign performance daily for optimization</p>
                <p>• A/B test different discount types to find what works best</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button variant="outline" fullWidth onClick={onClose}>
              Cancel
            </Button>
            <Button 
              variant="default" 
              fullWidth 
              disabled={!formData?.name || !formData?.discountType || !formData?.startDate || !formData?.endDate}
              onClick={handleSubmit}
            >
              Create Campaign
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCampaignModal;